/**
 * Custom Print Selector - Premium Edition with Gamification
 * Handles the complete flow of custom print selection, image upload, 
 * unique ID generation, and Shopify cart integration.
 * Updated to support Multi-Product Tabs (Hoodie/T-Shirt/Long Sleeve) and Instant Cart Refresh.
 */

class CustomPrintSelector {
  constructor() {
    this.formElement = document.getElementById('print-selector-form') || document.querySelector('.print-selector-form') || document.querySelector('form');
    this.currentStep = 1;
    this.currentType = 'hoodie'; 
    this.configData = window.customPrintConfig || {};
    
    this.currentPrintData = {
      productType: 'hoodie',
      location: null,
      variantId: null,
      variantTitle: null,
      image: null,
      imageUrl: null,
      notes: null,
      orderNo: null,
      locationName: null,
      color: null,
      colorName: null
    };
    this.queuedPrints = [];

    // DOM Elements for dynamic rendering
    this.locationsGrid = document.getElementById('dynamic-locations-grid');
    this.thumbnailsContainer = document.getElementById('dynamic-thumbnails-container'); 
    this.colorImagesContainer = document.getElementById('dynamic-color-images-container');
    this.previewSection = document.getElementById('selected-location-preview');
    this.sizeOptionsContainer = document.getElementById('size-options'); 

    this.initializeEventListeners();
    this.initializeTabs();
    this.loadProductType('hoodie'); // Initial Load
    this.addProgressTracking();
    this.initializeAnimations();
  }

  initializeTabs() {
    const tabs = document.querySelectorAll('.print-tab-btn');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        tabs.forEach(t => t.classList.remove('active'));
        e.target.classList.add('active');
        const newType = e.target.getAttribute('data-type');
        if (newType !== this.currentType) {
            this.switchProductType(newType);
        }
      });
    });
  }

  switchProductType(type) {
    this.currentType = type;
    this.currentPrintData.productType = type;
    this.currentPrintData.location = null;
    this.currentPrintData.locationName = null;
    this.currentPrintData.variantId = null;
    
    this.goToStep(1);
    this.loadProductType(type);
  }

  loadProductType(type) {
    const data = this.configData[type];
    if (!data) {
        console.error(`No configuration found for product type: ${type}`);
        return;
    }
    this.renderLocations(data.locations);
    this.currentVariants = data.variants;
  }

  renderLocations(locationsData) {
    this.locationsGrid.innerHTML = '';
    this.thumbnailsContainer.innerHTML = '';
    this.colorImagesContainer.innerHTML = '';
    this.thumbnailsContainer.appendChild(this.previewSection); 

    if (!locationsData || locationsData.length === 0) {
        this.locationsGrid.innerHTML = '<p>No print locations configured for this product.</p>';
        return;
    }

    locationsData.forEach((item, index) => {
        const id = index + 1;
        const locationCard = document.createElement('div');
        locationCard.className = 'print-location-card';
        locationCard.setAttribute('data-location-id', id);

        const imgHtml = item.main_image 
          ? `<img src="${item.main_image}" class="location-image" alt="${item.title}" loading="lazy" width="400">`
          : `<div class="placeholder-image"><svg width="80" height="80" viewBox="0 0 80 80" fill="none"><rect width="80" height="80" fill="#f3f4f6"/><text x="40" y="40" text-anchor="middle" dy=".3em" fill="#9ca3af" font-size="14">Location ${id}</text></svg></div>`;

        locationCard.innerHTML = `
          <div class="location-image-wrapper">${imgHtml}</div>
          <h3 class="location-title">${item.title}</h3>
          <button type="button" class="location-select-btn" data-location="${id}">Select This Location</button>
        `;
        
        locationCard.addEventListener('click', (e) => this.selectLocation(e));
        this.locationsGrid.appendChild(locationCard);

        const gallery = document.createElement('div');
        gallery.className = 'thumbnail-gallery';
        gallery.id = 'thumbnail-gallery-' + id;
        gallery.setAttribute('data-location', id);
        gallery.style.display = 'none';

        const createThumbBtn = (imgSrc, dataThumb, label) => {
             const btn = document.createElement('button');
             btn.type = 'button';
             btn.className = 'thumbnail-item' + (dataThumb === 'location' ? ' thumbnail-location-original' : '');
             btn.setAttribute('data-thumbnail', dataThumb);
             if (imgSrc) btn.innerHTML = `<img src="${imgSrc}" class="thumbnail-image" alt="${label}" loading="lazy" width="200">`;
             else btn.innerHTML = `<div class="thumbnail-placeholder">${label}</div>`;
             
             btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.handleThumbnailClick(btn);
             });
             return btn;
        };

        gallery.appendChild(createThumbBtn(item.main_image, 'location', 'L'));
        gallery.appendChild(createThumbBtn(item.thumb1, '1', '1'));
        gallery.appendChild(createThumbBtn(item.thumb2, '2', '2'));
        this.thumbnailsContainer.insertBefore(gallery, this.previewSection);

        const colorDiv = document.createElement('div');
        colorDiv.id = 'color-images-gallery-' + id;
        colorDiv.setAttribute('data-location', id);
        colorDiv.style.display = 'none';

        const createColorImg = (src, colorHex) => {
            const img = document.createElement('img');
            img.setAttribute('data-color', colorHex);
            img.src = src || '';
            img.style.display = 'none';
            return img;
        };

        colorDiv.appendChild(createColorImg(item.maroon_view, '330000'));
        colorDiv.appendChild(createColorImg(item.orange_view, 'c06b11'));
        colorDiv.appendChild(createColorImg(item.green_view, '274e13'));
        colorDiv.appendChild(createColorImg(item.black_view, '000000'));
        this.colorImagesContainer.appendChild(colorDiv);
    });
  }

  renderSizes() {
    this.sizeOptionsContainer.innerHTML = ''; 
    if (!this.currentVariants || this.currentVariants.length === 0) {
      this.sizeOptionsContainer.innerHTML = '<p class="error-text">No product variants found.</p>';
      return;
    }

    this.currentVariants.forEach((variant, index) => {
      const label = document.createElement('label');
      label.className = 'size-option';
      const disabledAttr = variant.available ? '' : 'disabled';
      const opacityStyle = variant.available ? '' : 'style="opacity: 0.5; cursor: not-allowed;"';
      const checked = index === 0 ? 'checked' : ''; 

      label.innerHTML = `
        <input type="radio" name="product-variant" value="${variant.id}" data-variant-title="${variant.title}" class="size-radio" ${checked} ${disabledAttr}>
        <span class="size-label" ${opacityStyle}>${variant.title}</span>
        <span class="size-checkmark">✓</span>
      `;
      this.sizeOptionsContainer.appendChild(label);
    });

    const sizeRadios = this.sizeOptionsContainer.querySelectorAll('.size-radio');
    sizeRadios.forEach(radio => {
        if(radio.checked) {
            this.currentPrintData.variantId = radio.value;
            this.currentPrintData.variantTitle = radio.getAttribute('data-variant-title');
        }
        radio.addEventListener('change', (e) => {
          if (e.target.checked) {
            this.currentPrintData.variantId = e.target.value;
            this.currentPrintData.variantTitle = e.target.getAttribute('data-variant-title');
            this.validateForm();
            
            const sizeOption = e.target.closest('.size-option');
            if (sizeOption) {
              sizeOption.style.transform = 'scale(1.05)';
              setTimeout(() => { sizeOption.style.transform = ''; }, 200);
            }
          }
        });
    });
  }

  addProgressTracking() {
    const container = document.querySelector('.print-container');
    if (container) container.classList.add(`step-${this.currentStep}`);
  }

  initializeAnimations() {}
  
  showCelebration() {
    const confettiContainer = document.createElement('div');
    confettiContainer.className = 'confetti-container';
    document.body.appendChild(confettiContainer);
    for (let i = 0; i < 40; i++) {
      const piece = document.createElement('div');
      piece.className = 'confetti-piece';
      piece.style.left = Math.random() * 100 + '%';
      piece.style.animationDelay = Math.random() * 2 + 's';
      piece.style.animationDuration = (Math.random() * 2 + 2) + 's';
      piece.style.background = Math.random() > 0.5 ? '#000' : '#fff';
      confettiContainer.appendChild(piece);
    }
    setTimeout(() => confettiContainer.remove(), 5000);
  }

  playSound(type = 'success') {
    if (type === 'success') this.showCelebration();
  }

  initializeEventListeners() {
    const backBtn = document.getElementById('back-to-location');
    if (backBtn) backBtn.addEventListener('click', () => this.goToStep(1));

    const colorRadios = document.querySelectorAll('.color-radio');
    colorRadios.forEach(radio => {
        if(radio.checked) {
             this.currentPrintData.color = radio.value;
             this.currentPrintData.colorName = radio.getAttribute('data-color-name');
        }
        radio.addEventListener('change', (e) => {
          if (e.target.checked) {
            this.currentPrintData.color = e.target.value;
            this.currentPrintData.colorName = e.target.getAttribute('data-color-name');
            this.switchColorImage(e.target.value);
            this.validateForm();
          }
        });
    });

    const imageInput = document.getElementById('print-image');
    if (imageInput) {
      imageInput.addEventListener('change', (e) => {
        if (e.target.files.length === 0) {
          this.currentPrintData.image = null;
          this.currentPrintData.imageUrl = null;
          this.validateForm();
        } else {
          this.handleImageUpload(e);
        }
      });
      
      const fileLabel = imageInput.nextElementSibling;
      if (fileLabel) {
        fileLabel.addEventListener('dragover', (e) => { e.preventDefault(); fileLabel.classList.add('drag-over'); });
        fileLabel.addEventListener('dragleave', () => { fileLabel.classList.remove('drag-over'); });
        fileLabel.addEventListener('drop', (e) => {
          e.preventDefault();
          fileLabel.classList.remove('drag-over');
          imageInput.files = e.dataTransfer.files;
          this.handleImageUpload({ target: imageInput });
        });
      }
    }

    const removeBtn = document.getElementById('remove-image');
    if (removeBtn) removeBtn.addEventListener('click', () => this.removeImage());

    const notesArea = document.getElementById('print-notes');
    if (notesArea) {
      notesArea.addEventListener('change', (e) => { this.currentPrintData.notes = e.target.value; });
    }

    const proceedBtn = document.getElementById('proceed-checkout-btn');
    if (proceedBtn) proceedBtn.addEventListener('click', () => this.proceedToCheckout());

    const addMoreBtn = document.getElementById('add-one-more-btn');
    if (addMoreBtn) addMoreBtn.addEventListener('click', () => this.addOneMore());

    const addToCartBtn = document.getElementById('add-to-cart-btn');
    if (addToCartBtn) addToCartBtn.addEventListener('click', () => this.addToCartInstantly());
  }

  selectLocation(event) {
    const card = event.target.closest('.print-location-card');
    if (!card) return; 
    
    const locationId = card.dataset.locationId;
    const locationTitle = card.querySelector('.location-title').textContent;

    document.querySelectorAll('.print-location-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    this.currentPrintData.location = locationId;
    this.currentPrintData.locationName = locationTitle;
    
    const locationImg = card.querySelector('.location-image-wrapper img');
    if (locationImg) {
      this.currentPrintData.locationImageUrl = locationImg.src;
    }

    this.renderSizes();
    this.showLocationColorGallery(locationId);

    const display = document.getElementById('selected-location-name');
    if (display) display.textContent = locationTitle;
    
    const typeDisplay = document.getElementById('selected-product-type-display');
    if (typeDisplay) typeDisplay.textContent = this.currentType.charAt(0).toUpperCase() + this.currentType.slice(1);

    const previewImg = document.getElementById('selected-location-image');
    if (previewImg && this.currentPrintData.locationImageUrl) {
      this.ensureNotEmpty(previewImg);
      previewImg.style.backgroundImage = `url('${this.currentPrintData.locationImageUrl}')`;
      previewImg.style.backgroundSize = 'cover';
      previewImg.style.backgroundPosition = 'center';
    }

    const checkedColor = document.querySelector('.color-radio:checked');
    if (checkedColor) {
      this.switchColorImage(checkedColor.value);
    }

    setTimeout(() => { this.goToStep(2); }, 300);
  }

  switchColorImage(colorValue) {
    const locationId = this.currentPrintData.location;
    if (!locationId) return;
    const gallery = document.querySelector(`#color-images-gallery-${locationId}`);
    if (!gallery) return;
    const colorImg = gallery.querySelector(`img[data-color="${colorValue}"]`);
    if (colorImg && colorImg.src) {
      const previewImg = document.getElementById('selected-location-image');
      if (previewImg) previewImg.style.backgroundImage = `url('${colorImg.src}')`;
    }
  }

  handleThumbnailClick(thumbnail) {
    const gallery = thumbnail.closest('.thumbnail-gallery');
    if (gallery) {
      gallery.querySelectorAll('.thumbnail-item').forEach(thumb => thumb.classList.remove('active'));
    }
    thumbnail.classList.add('active');
    const img = thumbnail.querySelector('.thumbnail-image');
    if (img && img.src) {
      const previewImg = document.getElementById('selected-location-image');
      if (previewImg) previewImg.style.backgroundImage = `url('${img.src}')`;
    }
  }

  showLocationColorGallery(locationId) {
    document.querySelectorAll('.thumbnail-gallery').forEach(gallery => gallery.style.display = 'none');
    const thumbnailGallery = document.querySelector(`#thumbnail-gallery-${locationId}`);
    if (thumbnailGallery) {
      thumbnailGallery.style.display = 'flex';
      const locationThumbnail = thumbnailGallery.querySelector('.thumbnail-item[data-thumbnail="location"]');
      if (locationThumbnail) {
         thumbnailGallery.querySelectorAll('.thumbnail-item').forEach(thumb => thumb.classList.remove('active'));
         locationThumbnail.classList.add('active');
         const locationThumbImg = locationThumbnail.querySelector('.thumbnail-image');
         if (locationThumbImg) {
            const previewImg = document.getElementById('selected-location-image');
            if (previewImg) previewImg.style.backgroundImage = `url('${locationThumbImg.src}')`;
         }
      }
    }
  }

  goToStep(step) {
    document.querySelectorAll('.print-step').forEach(el => el.classList.add('hidden'));
    const stepEl = document.getElementById(`step-${step === 1 ? 'location' : 'customize'}`);
    if (stepEl) {
      stepEl.classList.remove('hidden');
      setTimeout(() => { stepEl.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    }

    const container = document.querySelector('.print-container');
    if (container) {
      container.classList.remove('step-1', 'step-2');
      container.classList.add(`step-${step}`);
    }

    const backBtn = document.getElementById('back-to-location');
    if (backBtn) backBtn.style.display = step === 1 ? 'none' : 'inline-flex';
    
    const tabs = document.getElementById('product-type-tabs');
    if(tabs) {
        if(step === 2) tabs.classList.add('hidden');
        else tabs.classList.remove('hidden');
    }

    this.currentStep = step;
    if (step === 2) this.validateForm();
  }

  handleImageUpload(event) {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { this.showError('Max size 10MB'); return; }
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) { this.showError('Invalid file type'); return; }
    
    this.showUploadProgress();
    this.currentPrintData.image = file;

    const reader = new FileReader();
    reader.onloadstart = () => this.updateUploadProgress(0);
    reader.onprogress = (e) => { if(e.lengthComputable) this.updateUploadProgress(Math.round((e.loaded/e.total)*100)); };
    reader.onload = (e) => {
        setTimeout(() => {
            const { preview, previewImg } = this.ensurePreviewElements();
            if(previewImg.tagName === 'IMG') previewImg.src = e.target.result;
            else previewImg.style.backgroundImage = `url('${e.target.result}')`;
            preview.classList.remove('hidden');
            
            this.currentPrintData.dataUrl = e.target.result;
            
            this.uploadImageToServer(file).then(url => {
                this.currentPrintData.imageUrl = url;
                this.updateUploadProgress(100);
                setTimeout(() => this.hideUploadProgress(), 500);
                this.showSuccess('Image uploaded!');
                this.validateForm();
            }).catch(err => {
                this.showError('Upload failed');
                this.hideUploadProgress();
            });
        }, 300);
    };
    reader.readAsDataURL(file);
  }

  async uploadImageToServer(file) {
    const uploadEndpoint = document.getElementById('upload-endpoint')?.value;
    if (!uploadEndpoint) throw new Error('Endpoint missing');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('location', this.currentPrintData.location || '');
    formData.append('type', this.currentType); 
    
    const response = await fetch(uploadEndpoint, { method: 'POST', body: formData });
    if (!response.ok) throw new Error('Upload failed');
    const data = await response.json();
    return data.url || data.imageUrl || data.path;
  }
  
  ensurePreviewElements() {
    let preview = document.getElementById('image-preview');
    let previewImg = document.getElementById('preview-img');
    if (!preview) preview = document.querySelector('.image-preview');
    if (!previewImg) previewImg = document.querySelector('.uploaded-preview');
    
    if (previewImg && previewImg.tagName === 'DIV') this.ensureNotEmpty(previewImg);
    return { preview, previewImg };
  }
  
  ensureNotEmpty(el) {
    if (el.tagName === 'DIV' && !el.firstElementChild) {
        el.innerHTML = '<span style="display:none"></span>';
    }
  }

  showUploadProgress() { document.getElementById('upload-progress')?.classList.remove('hidden'); }
  hideUploadProgress() { setTimeout(() => document.getElementById('upload-progress')?.classList.add('hidden'), 500); }
  updateUploadProgress(p) { 
      const fill = document.getElementById('progress-fill');
      if(fill) fill.style.width = p + '%';
      const txt = document.getElementById('progress-text');
      if(txt) txt.textContent = `Uploading... ${p}%`;
  }

  validateForm() {
    const hasLocation = !!this.currentPrintData.location;
    const hasSize = !!this.currentPrintData.variantId;
    const hasColor = !!this.currentPrintData.color;
    const hasImage = !!this.currentPrintData.image; 
    
    const isValid = hasLocation && hasSize && hasColor && hasImage;
    
    const proceedBtn = document.getElementById('proceed-checkout-btn');
    if(proceedBtn) {
        proceedBtn.disabled = !isValid;
        
        const addMoreBtn = document.getElementById('add-one-more-btn');
        if(addMoreBtn) addMoreBtn.disabled = !isValid;
        
        const addToCartBtn = document.getElementById('add-to-cart-btn');
        if(addToCartBtn) addToCartBtn.disabled = !isValid;
        
        if(isValid) {
            proceedBtn.classList.add('ready');
            proceedBtn.style.animation = 'pulse 0.6s ease';
        } else {
            proceedBtn.classList.remove('ready');
            proceedBtn.style.animation = '';
        }
    }
  }

  async addOneMore() {
    if (!this.currentPrintData.location || !this.currentPrintData.variantId || !this.currentPrintData.color || !this.currentPrintData.image) {
      this.showError('Complete all fields first.');
      return;
    }
    
    if(!this.currentPrintData.imageUrl) {
        this.showError('Wait for upload to finish.');
        return;
    }

    try {
      const queuedItem = {
        productType: this.currentType, 
        variantId: this.currentPrintData.variantId,
        variantTitle: this.currentPrintData.variantTitle,
        location: this.currentPrintData.location,
        locationName: this.currentPrintData.locationName,
        imageUrl: this.currentPrintData.imageUrl,
        notes: this.currentPrintData.notes || '',
        color: this.currentPrintData.color,
        colorName: this.currentPrintData.colorName
      };
      
      this.queuedPrints.push(queuedItem);
      this.resetFormForNextItem();
      this.showSuccess('Design saved. Add another!');
      this.goToStep(1);
    } catch (e) {
      this.showError('Failed to queue.');
    }
  }

  resetFormForNextItem() {
    const imageInput = document.getElementById('print-image');
    if(imageInput) { imageInput.value = ''; imageInput.files = null; }
    this.currentPrintData.image = null;
    this.currentPrintData.imageUrl = null;
    const preview = document.getElementById('image-preview');
    if(preview) preview.classList.add('hidden');
    
    const notes = document.getElementById('print-notes');
    if(notes) notes.value = '';
    
    this.renderSizes(); 
    
    const firstColor = document.querySelector('.color-radio');
    if(firstColor) {
        firstColor.checked = true;
        this.currentPrintData.color = firstColor.value;
        this.currentPrintData.colorName = firstColor.getAttribute('data-color-name');
        this.switchColorImage(firstColor.value);
    }
    
    this.currentPrintData.location = null;
    this.validateForm();
  }
  
  removeImage() {
      const input = document.getElementById('print-image');
      if(input) input.value = '';
      this.currentPrintData.image = null;
      this.currentPrintData.imageUrl = null;
      document.getElementById('image-preview')?.classList.add('hidden');
      this.validateForm();
  }

  // --- CART ADDITION LOGIC REWRITTEN FOR INSTANT UPDATES ---

  async addItemsToCartAPI(itemsToAdd) {
    const formData = { items: itemsToAdd };
    
    // Shopify Dawn/2.0 Themes require "sections" to update the cart drawer natively
    const sectionsToUpdate = [];
    if (document.querySelector('cart-drawer')) sectionsToUpdate.push('cart-drawer');
    if (document.querySelector('cart-icon-bubble') || document.getElementById('cart-icon-bubble')) {
        sectionsToUpdate.push('cart-icon-bubble');
    }
    
    if(sectionsToUpdate.length > 0) {
        formData.sections = sectionsToUpdate.join(',');
        formData.sections_url = window.location.pathname;
    }

    const rootPath = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${rootPath}cart/add.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(formData)
    });

    if(!res.ok) throw new Error('Cart add failed');
    const cartResponse = await res.json();
    
    // Dispatch events to instantly open side-carts (Dawn + Third-Party themes)
    if (formData.sections) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'add', cartData: cartResponse } }));
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(cartResponse);
            if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
            if (typeof cartDrawer.open === 'function') cartDrawer.open();
        }
    }

    window.dispatchEvent(new Event('cart:refresh'));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: cartResponse } }));
    
    fetch(`${rootPath}cart.js`).then(r => r.json()).then(cart => {
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    });

    return cartResponse;
  }

  async addToCartInstantly() {
    if (!this.currentPrintData.imageUrl) { 
      this.showError('Wait for upload to finish.'); 
      return; 
    }

    const btn = document.getElementById('add-to-cart-btn');
    const originalText = btn.textContent;
    btn.classList.add('loading');
    btn.textContent = 'Adding...';
    btn.disabled = true;

    try {
      const orderNo = await this.createOrderInSheets();
      let itemsToAdd = [];

      for (const q of this.queuedPrints) {
        await this.storeInSheets(orderNo, q);
        const designId = q.imageUrl ? q.imageUrl.split('/').pop().split('.')[0] : 'N/A';
        itemsToAdd.push({
            id: q.variantId,
            quantity: 1,
            properties: {
                'PID No': orderNo,
                'Print Location': q.locationName,
                'Product Type': q.productType || this.currentType,
                'Print Color': q.colorName,
                'Design ID': designId
            }
        });
      }

      await this.storeInSheets(orderNo, this.currentPrintData);
      const currentDesignId = this.currentPrintData.imageUrl ? this.currentPrintData.imageUrl.split('/').pop().split('.')[0] : 'N/A';
      itemsToAdd.push({
          id: this.currentPrintData.variantId,
          quantity: 1,
          properties: {
              'PID No': orderNo,
              'Print Location': this.currentPrintData.locationName,
              'Product Type': this.currentPrintData.productType || this.currentType,
              'Print Color': this.currentPrintData.colorName,
              'Design ID': currentDesignId
          }
      });

      // Use Bulk Addition instead of multiple single requests
      await this.addItemsToCartAPI(itemsToAdd);

      this.queuedPrints = [];
      this.showSuccess('Added to cart successfully!');
      this.resetFormForNextItem();
      this.goToStep(1);

    } catch (e) {
      console.error(e);
      this.showError(e.message || 'Failed to add to cart');
    } finally {
      btn.classList.remove('loading');
      btn.textContent = originalText;
      this.validateForm();
    }
  }

  async proceedToCheckout() {
    if (!this.currentPrintData.imageUrl) { 
        this.showError('Wait for upload.'); 
        return; 
    }

    const proceedBtn = document.getElementById('proceed-checkout-btn');
    proceedBtn.classList.add('loading');
    proceedBtn.disabled = true;

    try {
      await this.clearCart();
      const orderNo = await this.createOrderInSheets();
      let itemsToAdd = [];

      for (const q of this.queuedPrints) {
        await this.storeInSheets(orderNo, q);
        const designId = q.imageUrl ? q.imageUrl.split('/').pop().split('.')[0] : 'N/A';
        itemsToAdd.push({
            id: q.variantId,
            quantity: 1,
            properties: {
                'PID No': orderNo,
                'Print Location': q.locationName,
                'Product Type': q.productType || this.currentType,
                'Print Color': q.colorName,
                'Design ID': designId
            }
        });
      }

      await this.storeInSheets(orderNo, this.currentPrintData);
      const currentDesignId = this.currentPrintData.imageUrl ? this.currentPrintData.imageUrl.split('/').pop().split('.')[0] : 'N/A';
      itemsToAdd.push({
          id: this.currentPrintData.variantId,
          quantity: 1,
          properties: {
              'PID No': orderNo,
              'Print Location': this.currentPrintData.locationName,
              'Product Type': this.currentPrintData.productType || this.currentType,
              'Print Color': this.currentPrintData.colorName,
              'Design ID': currentDesignId
          }
      });

      await this.addItemsToCartAPI(itemsToAdd);
      window.location.href = '/checkout';

    } catch (e) {
      console.error(e);
      this.showError(e.message || 'Checkout failed');
    } finally {
      proceedBtn.classList.remove('loading');
      proceedBtn.disabled = false;
    }
  }
  
  // -- FALLBACK ERROR HANDLING ADDED -- 
  // Ensures Cart functionality works even if Google Sheets is missing/broken.

  async createOrderInSheets() {
     const ep = document.getElementById('sheets-endpoint')?.value;
     if(!ep) return 'ORD-' + Math.floor(Math.random() * 1000000); 
     
     try {
         const res = await fetch(ep, { method: 'POST', body: JSON.stringify({generateOrderOnly:true})});
         const d = await res.json();
         if(!d.success) return 'ORD-' + Math.floor(Math.random() * 1000000); 
         return d.orderNo;
     } catch(e) {
         console.warn("Sheets error, falling back to local order generation.", e);
         return 'ORD-' + Math.floor(Math.random() * 1000000);
     }
  }
  
  async storeInSheets(orderNo, item) {
      const ep = document.getElementById('sheets-endpoint')?.value;
      if(!ep) return;
      try {
          const payload = {
              orderNo: orderNo,
              location: item.locationName,
              type: item.productType || this.currentType, 
              variant: item.variantTitle,
              color: item.colorName,
              imageUrl: item.imageUrl,
              notes: item.notes,
              timestamp: new Date().toISOString(),
              customerEmail: this.getCustomerEmail()
          };
          await fetch(ep, { method: 'POST', body: JSON.stringify(payload) });
      } catch(e) {
          console.warn('Failed to store in sheets, continuing anyway.', e);
      }
  }

  async clearCart() {
     const rootPath = window.Shopify?.routes?.root || '/';
     await fetch(`${rootPath}cart/clear.js`, { method: 'POST' });
  }

  showSuccess(msg) { 
     const el = document.getElementById('success-message');
     if(el) {
         el.classList.remove('hidden');
         document.getElementById('success-text').textContent = msg;
         this.playSound();
         setTimeout(() => el.classList.add('hidden'), 4000);
     }
  }

  showError(msg) {
     const el = document.getElementById('error-message');
     if(el) {
         el.classList.remove('hidden');
         document.getElementById('error-text').textContent = msg;
         setTimeout(() => el.classList.add('hidden'), 5000);
     }
  }
  
  getCustomerEmail() { return window.customerData?.email || 'Guest'; }
}

// Init
(() => {
  const init = () => {
    if (window.__customPrintSelectorInitialized) return;
    window.__customPrintSelectorInitialized = true;
    new CustomPrintSelector();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();